import React, { FC } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  AlertDetail,
  AlertIconProps,
  AlertIconType,
} from "components/portfolio/components/alert/alert-icon-type";
import {
  getAlertIconListViewConfig,
  getShowDetailByType,
} from "components/portfolio/components/alert/AlertIconListConfig";
import { Skeleton } from "@mui/material";
import { useAxios } from "myAxios";
import { AnimatePresence, motion } from "framer-motion";
import { dismissCallback } from "components/portfolio/components/alert/index";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Tooltip } from "@mui/material";

const useStyles = makeStyles(() => ({
  listContainer: {
    maxHeight: "52vh",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 4,
    },
    paddingRight: 2,
  },
  cardContainer: {
    borderRadius: 6,
    border: "1px solid #DEDEDE",
    marginTop: 8,
  },
  itemContainer: {
    display: "flex",
    width: "100%",
    marginTop: 8,
    paddingLeft: 12,
  },
  itemLabel: {
    fontSize: 12,
    color: "#898989",
    lineHeight: "16px",
    width: 100,
    display: "flex",
    alignItems: "center",
  },
  itemValue: {
    fontSize: 12,
    lineHeight: "16px",
    color: "#333333",
    display: "flex",
    alignItems: "center",
  },
  itemActionContainer: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid #EDEDED",
    padding: "6px 12px",
    marginTop: 12,
  },
  action: {
    fontSize: 12,
    color: "#00A0D1",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  tooltip: {
    maxHeight: "30vh",
  },
  iconStyle: {
    fontSize: "12px",
    color: "#B2B2B2",
    marginLeft: "4px",
  },
}));

interface AlertIconListProps extends AlertIconProps {
  type: AlertIconType;
  alerts?: AlertDetail[];
}

const AlertIconList: FC<AlertIconListProps> = ({
  type,
  alerts,
  gridEvent,
  mutateKeys,
  accountLevelMutateKeys,
  accountKey = "",
}) => {
  const classes = useStyles();
  const configs = getAlertIconListViewConfig(type);
  const axios = useAxios();

  const dismiss = (alertId: string) => () => {
    const payload = { alertId };
    axios.post("/api/dataservice/v2/alerts/dismiss-alert", payload).then(() => {
      dismissCallback(
        { alertId, alertType: type, accountKeys: [accountKey] },
        gridEvent,
        mutateKeys,
        accountLevelMutateKeys
      );
    });
  };

  const showDetail = getShowDetailByType(type);

  return (
    <div className={classes.listContainer}>
      {alerts === undefined ? (
        <div className={classes.cardContainer}>
          {Array.from({ length: 4 }).map((_x, i) => (
            <div
              className={classes.itemContainer}
              key={i}
            >
              <div className={classes.itemLabel}>
                <Skeleton width={80} />
              </div>
              <div className={classes.itemValue}>
                <Skeleton width={80} />
              </div>
            </div>
          ))}
          <div className={classes.itemActionContainer}>
            <div className={classes.action}>
              <Skeleton width={80} />
            </div>
            <div className={classes.action}>
              <Skeleton width={80} />
            </div>
          </div>
        </div>
      ) : (
        <AnimatePresence initial={false}>
          {alerts.map((alert) => (
            <motion.div
              exit={{ opacity: 0 }}
              className={classes.cardContainer}
              key={alert.id}
            >
              {configs?.map((config) => (
                <div
                  className={classes.itemContainer}
                  key={config.label}
                >
                  <div className={classes.itemLabel}>
                    {config.label}
                    {config.tooltip ? (
                      <Tooltip
                        arrow={true}
                        placement="bottom"
                        title={config.tooltip}
                        classes={{ tooltip: classes.tooltip }}
                      >
                        <ErrorOutlineIcon className={classes.iconStyle} />
                      </Tooltip>
                    ) : null}
                  </div>
                  <div
                    className={classes.itemValue}
                    style={{ ...config?.valueStyle }}
                  >
                    {config.valueFormatter
                      ? config.valueFormatter(alert[config.colId] as string, alert)
                      : alert[config.colId]}
                  </div>
                </div>
              ))}
              <div className={classes.itemActionContainer}>
                <div
                  className={classes.action}
                  onClick={dismiss(alert.id)}
                >
                  Dismiss
                </div>
                {configs?.filter((c) => c.label === "Reason")?.length ? null : (
                  <div
                    className={classes.action}
                    onClick={() => showDetail(alert)}
                  >
                    Show Details {">"}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      )}
    </div>
  );
};

export default AlertIconList;
